import { createUseStyles, withTheme } from '@glu/theming';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import moment from 'moment';
import styles from './RangeSelect.styles';

const useStyles = createUseStyles(styles);

const RangeSelect = ({
  RangeSelectProps,
  alwaysShowCalendars,
  firstFocusedElement,
  htmlId,
  onApply,
  onPresetSelection,
  rangePresets,
  selectedRangeName,
  setEditing,
  setHead,
  setSelectedEndDate,
  setSelectedRangeName,
  setSelectedStartDate,
  showCustomRangeLabel,
  ...rest
}) => {
  const classes = useStyles(rest);
  const buttonRef = useRef();
  const presets = Object.keys(rangePresets).map((key) => ({
    name: key,
    range: [
      moment(rangePresets[key][0]),
      moment(rangePresets[key][1])
    ]
  }));

  const handlePresetClick = ({ range: [start, end], name }) => {
    onPresetSelection(name, [start, end]);
    setSelectedRangeName(name);
    setSelectedStartDate(start);
    setSelectedEndDate(end);
    if (alwaysShowCalendars) {
      setHead(start);
      setEditing(true);
    } else {
      onApply(start, end);
    }
  };

  const handleCustomClick = () => {
    onPresetSelection(null);
    setSelectedRangeName('custom');
    setEditing(true);
  };

  /* istanbul ignore next */
  useEffect(() => {
    if (firstFocusedElement) {
      buttonRef.current.focus();
    }
  }, [firstFocusedElement]);

  const refFunction = (key) => {
    const buttonProps = {};
    if (!key) buttonProps.ref = buttonRef;
    return buttonProps;
  };

  return (
    <div data-qa="range-select" className={`${classes.root}`} role="listbox">
      {presets.map((preset, key) => (
        <button
          {...refFunction(key)}
          aria-selected={preset.name === selectedRangeName}
          key={preset.name}
          data-qa={`range-option-${[preset.name]}`}
          id={`${htmlId}-range-option-${[preset.name]}`}
          className={`${classes.option} ${preset.name === selectedRangeName ? classes.selected : ''}`}
          onClick={() => {
            handlePresetClick(preset);
          }}
          role="option"
          type="button"
        >
          {preset.name}
        </button>
      ))}
      {showCustomRangeLabel && (
        <button
          aria-selected={selectedRangeName === 'custom'}
          id={`${htmlId}-range-option-custom`}
          key="custom"
          data-qa="range-option-custom"
          className={`${classes.option} ${selectedRangeName === 'custom' ? classes.selected : ''}`}
          onClick={handleCustomClick}
          role="option"
          type="button"
        >
          Custom Range
        </button>
      )}
    </div>
  );
};

RangeSelect.propTypes = {
  RangeSelectProps: PropTypes.shape({}),
  alwaysShowCalendars: PropTypes.bool.isRequired,
  firstFocusedElement: PropTypes.bool.isRequired,
  htmlId: PropTypes.string,
  onApply: PropTypes.func.isRequired,
  onPresetSelection: PropTypes.func.isRequired,
  rangePresets: PropTypes.shape({}).isRequired,
  selectedRangeName: PropTypes.string,
  setEditing: PropTypes.func.isRequired,
  setHead: PropTypes.func.isRequired,
  setSelectedEndDate: PropTypes.func.isRequired,
  setSelectedRangeName: PropTypes.func.isRequired,
  setSelectedStartDate: PropTypes.func.isRequired,
  showCustomRangeLabel: PropTypes.bool.isRequired
};

RangeSelect.defaultProps = {
  RangeSelectProps: {},
  htmlId: undefined,
  selectedRangeName: undefined
};

export default withTheme(RangeSelect);
